import { Armchair, Mic, BookOpen, Users, Ship, CalendarDays, MapPin, Anchor, Bed, FileText, Music } from "lucide-react";
import { FadeUp, SectionHeading } from "../components/motion";

const REST = [
  { icon: Armchair, label: "Quiet time in your room or balcony" },
  { icon: Mic, label: "Encouragement from a guest speaker" },
  { icon: BookOpen, label: "Worship and reflection" },
  { icon: Users, label: "Community and connection" },
  { icon: Ship, label: "Time to enjoy the ship and shore excursions" },
];

const PLANNING = [
  {
    icon: CalendarDays,
    id: "dates",
    title: "Dates, ship, and length",
    body: "Final cruise dates, ship, and retreat length will be shared once confirmed.",
  },
  {
    icon: MapPin,
    id: "getting-there",
    title: "Getting there",
    body: "Departure port, nearby airports, and ground transportation will be announced once the cruise is confirmed.",
  },
  {
    icon: Anchor,
    id: "route",
    title: "Route and ports",
    body: "The sailing route and ports of call will be announced once the itinerary is confirmed.",
  },
  {
    icon: Bed,
    id: "cabins",
    title: "Cabins and pricing",
    body: "Final room options, rates, and payment details will be announced once confirmed. No payments or reservations are being taken.",
  },
];

const DESTINATIONS = [
  {
    id: "los-angeles",
    name: "Port of Los Angeles",
    img: "/port-of-los-angeles.jpg",
    alt: "Illustrative photo: a large white cruise ship docked at the Los Angeles Cruise Terminal behind tall palm trees",
  },
  {
    id: "catalina",
    name: "Catalina Island",
    img: "/catalina-island.jpg",
    alt: "Illustrative photo: aerial view of Avalon Harbor on Catalina Island, with boats on turquoise water beneath green hills",
  },
  {
    id: "ensenada",
    name: "Ensenada, Mexico",
    img: "/ensenada-mexico.jpg",
    alt: "Illustrative photo: aerial view of a cruise ship docked beside the marina and waterfront in Ensenada, Mexico",
  },
];

export default function RenewalOverview() {
  return (
    <section
      id="renewal"
      data-testid="section-renewal-overview"
      className="grain relative overflow-hidden bg-navy py-24 text-cream sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-5">
            <SectionHeading
              eyebrow="Featured Sailing"
              titleLines={["Chaplain Renewal", "at Sea."]}
            />
            <FadeUp delay={0.1}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-cream/75" data-testid="renewal-why">
                <span className="font-serif text-lg text-cream">Why this retreat?</span>
                <br />
                Chaplaincy asks us to make room for others. This retreat will offer chaplains
                space to rest, worship, reflect, and reconnect with a community that understands
                their work.
              </p>
            </FadeUp>
            <FadeUp delay={0.18}>
              <p className="mt-8 max-w-md border-l-2 border-gold/60 pl-5 text-sm leading-relaxed text-cream/70" data-testid="renewal-program-note">
                Chaplain Renewal at Sea is its own dedicated sailing with its own program &mdash;
                light, optional, and theme-specific &mdash; with generous free time for guests to
                enjoy the ship and ports.
              </p>
            </FadeUp>
          </div>

          <div className="min-w-0 lg:col-span-7">
            <FadeUp>
              <h3 className="font-serif text-2xl text-cream" data-testid="renewal-rest-heading">
                Rest can look different for everyone.
              </h3>
              <ul className="mt-6 divide-y divide-gold/15 border-y border-gold/15" data-testid="renewal-rest-list">
                {REST.map((r, i) => (
                  <li
                    key={r.label}
                    data-testid={"renewal-rest-item-" + (i + 1)}
                    className="flex items-center gap-5 py-4"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-gold/25">
                      <r.icon size={17} strokeWidth={1.25} className="text-gold-light" aria-hidden="true" />
                    </span>
                    <span className="text-sm font-light text-cream/85 sm:text-base">{r.label}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>

            <FadeUp delay={0.06}>
              <div className="mt-10 flex gap-4 border border-gold/30 bg-navy-abyss/50 p-5 sm:p-6" data-testid="concert-block">
                <Music size={20} strokeWidth={1.25} className="mt-0.5 shrink-0 text-gold-light" aria-hidden="true" />
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    <h3 className="font-serif text-xl text-cream">A possible evening concert</h3>
                    <span className="border border-gold/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold-light">
                      To be announced
                    </span>
                  </div>
                  <p className="mt-2.5 text-sm font-light leading-relaxed text-cream/75">
                    As a special evening activity, we are exploring a live concert or inspirational
                    music event &mdash; kept separate from the daytime retreat sessions. The performer
                    and details are to be announced, and the concert is not yet confirmed; nothing is
                    promised or included in any price at this stage. Guests will still have free time
                    to enjoy the ship and ports.
                  </p>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <h3 className="mt-10 font-serif text-2xl text-cream" data-testid="renewal-planning-heading">
                Planning ahead.
              </h3>
              <div className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="renewal-planning-grid">
                {PLANNING.map((p) => (
                  <article
                    key={p.id}
                    data-testid={"renewal-planning-" + p.id}
                    className="flex h-full flex-col border border-gold/25 bg-navy-abyss/60 p-5"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p.icon size={18} strokeWidth={1.25} className="text-gold-light" aria-hidden="true" />
                      <span className="border border-gold/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold-light">
                        To be announced
                      </span>
                    </div>
                    <h4 className="mt-3 font-serif text-base text-cream">{p.title}</h4>
                    <p className="mt-1.5 text-sm font-light leading-relaxed text-cream/70">{p.body}</p>
                  </article>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.14}>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.24em] text-gold-light" data-testid="route-photo-caption">
                Possible destinations
              </p>
              <div
                className="mt-3 flex w-full max-w-full snap-x snap-mandatory gap-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:grid-cols-3 sm:gap-5 sm:overflow-visible"
                data-testid="route-photo-grid"
              >
                {DESTINATIONS.map((d) => (
                  <figure
                    key={d.id}
                    data-testid={"route-photo-" + d.id}
                    className="flex w-[78%] shrink-0 snap-start flex-col border border-gold/20 bg-navy/40 sm:w-auto sm:shrink"
                  >
                    <img src={d.img} alt={d.alt} width={1000} height={1000} loading="lazy" className="h-auto w-full" />
                    <figcaption className="flex items-center justify-between gap-2 p-3">
                      <span className="font-serif text-sm text-cream">{d.name}</span>
                      <span className="shrink-0 border border-gold/30 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-gold-light">
                        Illustrative only
                      </span>
                    </figcaption>
                  </figure>
                ))}
              </div>
              <p className="mt-3 text-xs leading-relaxed text-cream/60" data-testid="route-note">
                Destinations and photos are illustrative only &mdash; the ship, departure port, and
                itinerary have not been confirmed. Final ship, departure port, and itinerary to be
                announced.
              </p>
            </FadeUp>
          </div>
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-12 flex gap-5 border border-gold/30 bg-navy-abyss/50 p-7 sm:p-8" data-testid="renewal-passport-note">
            <FileText size={22} strokeWidth={1.25} className="mt-0.5 shrink-0 text-gold-light" aria-hidden="true" />
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold-light">Passport note</p>
              <p className="mt-3 text-sm leading-relaxed text-cream/85">
                A valid passport book will be required for participants. Final travel-document
                instructions will be provided once the cruise and itinerary are confirmed.
              </p>
              <p className="mt-3 text-xs leading-relaxed text-cream/60">
                The State Department notes that cruise lines may require a passport book and
                recommends one for cruise passengers.
              </p>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div className="mt-12 flex flex-col items-start gap-6 border-t border-gold/20 pt-10 sm:flex-row sm:items-center sm:justify-between">
            <a
              href="https://chaplainatsea.com/interest-form"
              data-testid="renewal-cta-link"
              className="inline-flex items-center justify-center bg-gold px-8 py-4 font-mono text-[12px] uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-gold-light"
            >
              Share your interest
            </a>
            <p className="max-w-sm text-xs leading-relaxed text-cream/65" data-testid="renewal-cta-note">
              This is an interest form, not a booking &mdash; sharing your interest carries no
              payment or reservation.
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
